import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useAccountSetup, usePasswordRecovery } from './hooks';
import MultiStepProgressBar from './MultiStepProgressBar';
import AccountInfo from './AccountInfo';
import PasswordReset from './PasswordReset';
import SecurityQuestion from './SecurityQuestion';
import OneTimePassword from './OneTimePassword';
import Disclaimer from './Disclaimer';
import TermsAndPolicy from './TermsAndPolicy';
import SetupSuccess from './SetupSuccess';
import CustomClientError from '../../errorhandling/CustomClientError';
import { Link } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { HorizontalDivider } from 'src/Admin/components/common/HorizontalDivider';
import RegistrationErrorMsg from './RegistrationErrorMsg';
import AlreadyRegisteredErrorMsg from './AlreadyRegisteredErrorMsg';
import { CoverageSVG } from '../../img/GlobalSVG';
import Skeleton from 'src/Member/components/footer/Skeleton';
import backArrowIcon from '../../img/arrow_back.svg';
import './AccountSetup.scss';

const AccountSetup = () => {
  const {
    currentStep,
    schema,
    passwordSchema,
    securityQuestionschema,
    passwordRules,
    showPassword,
    showConfirmPassword,
    userData,
    formData,
    privacyPolicyContent,
    termsConditionContent,
    isButtonDisable,
    nextStep,
    prevStep,
    setShowPassword,
    setShowConfirmPassword,
    handleInput,
    updateUser,
    getStepTitle,
    selectedQuestionValues,
    handleQuestionValChange,
    countryCodeList,
    handleFormData,
    updateSecurityQuestions,
    showSkeletonLoader,
    registrationUrlValid,
    isRegistered,
    serverError,
    handleSecurityQuestionsError,
    fetchSecurityQuestions,
    insurerClientSettings,
  } = useAccountSetup();

  const {
    email,
    OTPExpiry,
    OTPLength,
    OTPAttempts,
    userDetails,
    skeletonLoader,
    setUserDetails,
    setEmail,
  } = usePasswordRecovery();

  const BackButton = () => {
    return currentStep !== 1 && currentStep !== 7 ? (
      <div
        role={'button'}
        className="pe-auto"
        onClick={prevStep}
        tabIndex={0}
        onKeyPress={(event) => {}}
      >
        <img
          className="position-relative mr-3"
          src={backArrowIcon}
          alt="logo"
        />
      </div>
    ) : currentStep === 1 ? (
      <Link to="/admin">
        <img
          className="position-relative mr-3"
          src={backArrowIcon}
          alt="logo"
        />
      </Link>
    ) : null;
  };
  return (
    <Container className="account-setup-container">
      {!isRegistered ? (
        !registrationUrlValid ? (
          <>
            <Row>
              <Col className="p-0">
                {currentStep !== 7 && (
                  <>
                    <div className="page-header d-flex align-items-center">
                      <BackButton />
                      <h1 className="register-header mb-2">{getStepTitle()}</h1>
                    </div>
                    <MultiStepProgressBar currentStep={currentStep} />
                  </>
                )}
              </Col>
            </Row>
            {!showSkeletonLoader ? (
              <Row className={currentStep === 1 && 'grid-template'}>
                <Col className="p-0">
                  <div className="page-content-container">
                    {currentStep === 1 && (
                      <AccountInfo
                        currentStep={currentStep}
                        nextStep={nextStep}
                        schema={schema}
                        userData={userData}
                        formData={formData}
                        handleInput={handleInput}
                        countryCodeList={countryCodeList}
                        serverError={serverError}
                      />
                    )}

                    {currentStep === 2 && (
                      <PasswordReset
                        currentStep={currentStep}
                        nextStep={nextStep}
                        passwordSchema={passwordSchema}
                        passwordRules={passwordRules}
                        showPassword={showPassword}
                        showConfirmPassword={showConfirmPassword}
                        setShowPassword={setShowPassword}
                        setShowConfirmPassword={setShowConfirmPassword}
                        formData={formData}
                        handleInput={handleInput}
                      />
                    )}

                    {currentStep === 3 ? (
                      !fetchSecurityQuestions ? (
                        <SecurityQuestion
                          currentStep={currentStep}
                          nextStep={nextStep}
                          securityQuestionschema={securityQuestionschema}
                          handleQuestionValChange={handleQuestionValChange}
                          selectedQuestionValues={selectedQuestionValues}
                          formData={formData}
                          handleInput={handleInput}
                          isButtonDisable={isButtonDisable}
                          handleFormData={handleFormData}
                          handleSecurityQuestionsError={
                            handleSecurityQuestionsError
                          }
                        />
                      ) : (
                        <CustomClientError
                          ErrorHeading="Something went wrong with request!"
                          ErrorMessage="Please try again."
                        />
                      )
                    ) : null}

                    {currentStep === 4 && (
                      <OneTimePassword
                        email={formData.email}
                        currentStep={currentStep}
                        nextStep={nextStep}
                        prevStep={prevStep}
                        OTPExpiry={
                          OTPExpiry === 0
                            ? insurerClientSettings?.OTPExpiresIn
                            : OTPExpiry
                        }
                        OTPLength={
                          OTPLength === 0
                            ? insurerClientSettings?.OTPLength
                            : OTPLength
                        }
                        OTPAttempts={OTPAttempts}
                      />
                    )}

                    {currentStep === 5 && (
                      <Disclaimer
                        currentStep={currentStep}
                        nextStep={nextStep}
                        content={privacyPolicyContent}
                      />
                    )}

                    {currentStep === 6 && (
                      <TermsAndPolicy
                        currentStep={currentStep}
                        nextStep={nextStep}
                        updateUser={updateUser}
                        termsContent={termsConditionContent}
                        formData={formData}
                        updateSecurityQuestions={updateSecurityQuestions}
                      />
                    )}

                    {currentStep === 7 && (
                      <SetupSuccess
                        currentStep={currentStep}
                        nextStep={nextStep}
                      />
                    )}
                  </div>
                </Col>
                {currentStep === 1 && (
                  <Col className="p-0">
                    <div className="column-section-right">
                      <div className="d-flex">
                        <div>
                          <CoverageSVG.BulbSVG />
                        </div>
                        <div className="ml-4 mr-4">
                          <h2>Welcome!</h2>
                          {/*<p className="note-desc">Create any User (Insurer, Employer, Broker, or Provider).</p> */}
                        </div>
                      </div>
                      <div className="mt-4 mb-4 divider">
                        <HorizontalDivider />
                      </div>
                      <div>
                        <p>
                          {' '}
                          This portal helps you manage your actions in one place
                          with easy-to-use management tools.{' '}
                        </p>

                        <p>
                          You can manage members, claim and download ID cards,
                          run reports, upload relevant documents and much more.{' '}
                        </p>

                        <p>
                          You can also configure settings, set up preferences,
                          create users, assign and view permission groups, and
                          more.
                        </p>
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            ) : (
              <Skeleton count={10} />
            )}
          </>
        ) : (
          <RegistrationErrorMsg />
        )
      ) : (
        <AlreadyRegisteredErrorMsg />
      )}
      <div className="toastify">
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
    </Container>
  );
};

export default AccountSetup;
